import { inject, Injectable } from '@angular/core';
import { WIZARD_CONTROLLER } from '@mwe/features/wizard';
import { catchError, EMPTY, Observable } from 'rxjs';
import { IkpPopupService } from '@shared-ikp/service/popup/popup.service';
import { markAllAsTouched } from '@shared-ikp/utils/signal-forms.util';
import { Ummeldung } from '../../../generated/model/ummeldung.model';
import { UmmeldungStore } from './ummeldung.store';
import type { RegisteredWizardStepForm } from './utils';
import AktuellerProzessSchrittEnum = Ummeldung.AktuellerProzessSchrittEnum;

// This service depends on the wizard-local WIZARD_CONTROLLER, so it is provided by UmmeldungWizardModule.
// In Angular v22 we can then use @Service() reduces boilerplate
// eslint-disable-next-line @angular-eslint/use-injectable-provided-in
@Injectable()
export class UmmeldungWizardService {
  private readonly store = inject(UmmeldungStore);
  private readonly wizardController = inject(WIZARD_CONTROLLER);
  private readonly popupService = inject(IkpPopupService);

  private readonly forms = new Map<unknown, RegisteredWizardStepForm>();
  private skipNextExitConfirmation = false;

  registerCurrentStepForm(stepForm: RegisteredWizardStepForm): { stepId: unknown | null; unregister: () => void } {
    const stepId = this.currentStepId();
    if (stepId === null) {
      return { stepId, unregister: () => undefined };
    }

    this.forms.set(stepId, stepForm);
    return {
      stepId,
      unregister: () => {
        if (this.forms.get(stepId) === stepForm) {
          this.forms.delete(stepId);
        }
      },
    };
  }

  touchAndValidateCurrentStep(): boolean {
    const field = this.currentStepForm()?.field;
    if (!field) {
      return true;
    }

    markAllAsTouched(field);
    return !field().invalid();
  }

  commitCurrentStepDraft(): void {
    this.currentStepForm()?.commitDraft();
  }

  isCurrentStep(stepId: unknown): boolean {
    return stepId !== null && this.currentStepId() === stepId;
  }

  trySaveDraft(): Observable<void> | null {
    if (this.store.loading() || !this.touchAndValidateCurrentStep()) {
      return null;
    }

    this.commitCurrentStepDraft();

    const aktuellerProzessSchritt = this.wizardController.currentStep()?.stepId as AktuellerProzessSchrittEnum | undefined;

    return this.store.saveActiveUmmeldung(aktuellerProzessSchritt ?? null).pipe(
      catchError(() => {
        this.popupService.handleErrorPopup(
          'save-ummeldung-error',
          'ummeldung.saveErrorPopup.title',
          'ummeldung.saveErrorPopup.message',
          true,
        );
        return EMPTY;
      }),
    );
  }

  closeWithoutExitConfirmation(reason: 'closed' | 'finished' = 'closed'): void {
    this.skipNextExitConfirmation = true;
    this.wizardController.close(reason);
  }

  consumeNextExitConfirmationBypass(): boolean {
    const shouldBypass = this.skipNextExitConfirmation;
    this.skipNextExitConfirmation = false;
    return shouldBypass;
  }

  private currentStepForm(): RegisteredWizardStepForm | undefined {
    const stepId = this.currentStepId();
    return stepId === null ? undefined : this.forms.get(stepId);
  }

  private currentStepId(): unknown {
    return this.wizardController.currentStep()?.stepId ?? null;
  }
}
